import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import '../styles/responsive/pages/Payouts.css';
import '../styles/enterprise.css';
import useDebounce from '../hooks/useDebounce';
import { useGetPlanQuery } from '../store/api/planApi';
import {
  X,
  AlertCircle,
  Send,
  Wallet,
  MoreVertical,
  ArrowUpRight,
  Repeat,
  CheckCircle2,
  Clock,
  XCircle,
  Copy,
  Check,
  Download,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Percent,
  Settings as SettingsIcon,
} from 'lucide-react';
import { formatUSD, formatCrypto, formatCount, truncateHash } from '../utils/formatAmount';
import { fetchWithAuth } from '../utils/api';
import toast from 'react-hot-toast';
import PageHeaderPro from '../components/common/PageHeaderPro';
import StatCard, { StatCardGrid } from '../components/common/StatCard';
import FilterTabs from '../components/common/FilterTabs';
import { TokenChainLogo } from '../components/common/CryptoLogo';
import { tokenLogoUrl, tokenColor, tokenInitials } from '../utils/assetLogos';
import AvailableAssets, { assetKey } from '../components/payouts/AvailableAssets';
import Pager from '../components/common/Pager';
import EntSearch from '../components/common/EntSearch';
import StatusBadge from '../components/common/StatusBadge';
import AssetSelect from '../components/common/AssetSelect';
import { TableSkeleton } from '../components/common/Skeleton';
import useFeature from '../hooks/useFeature';
import usePremiumUpsell from '../hooks/usePremiumUpsell';
import PremiumUpsellModal from '../components/common/PremiumUpsellModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Padding for the balance card's coin cluster when a merchant holds fewer than
// three distinct assets. Decorative only — never a balance or a support claim.
const HERO_FALLBACK = ['USDT', 'ETH', 'BTC'];

// One floating coin in the balance card's artwork. A token with no CDN logo
// falls back to an initials disc rather than a broken image.
const AssetSpot = ({ symbol }) => {
  const [failed, setFailed] = useState(false);
  const url = tokenLogoUrl(symbol);
  if (url && !failed) {
    return <img src={url} alt="" onError={() => setFailed(true)} />;
  }
  return (
    <span className="po-bal__coin-fallback" style={{ background: tokenColor(symbol) }}>
      {tokenInitials(symbol)}
    </span>
  );
};

const Payouts = () => {
  const { token } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const { data: plan } = useGetPlanQuery();
  const hasPriority = useFeature('priority_payouts');
  const upsell = usePremiumUpsell();
  const navigate = useNavigate();

  // Auto-conversion status (read-only summary; managed on its own page).
  const [autoConv, setAutoConv] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        // Auto-conversion is a mainnet feature, so the summary is only
        // meaningful in Live. Asking for it in Test previously returned the
        // mainnet row and reported a target that does not apply there.
        const r = await fetchWithAuth(
          `${API_BASE_URL}/merchant/auto-conversion?is_testnet=${mode === 'test' ? 'true' : 'false'}`
        );
        const d = await r.json();
        if (d.success) setAutoConv(d.data?.config || null);
      } catch (e) {
        /* non-blocking */
      }
    })();
    // Refetch on mode change, or the strip keeps showing the other network
    // kind's configuration after the merchant switches.
  }, [mode]);

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    completedRequests: 0,
    rejectedRequests: 0,
    cancelledRequests: 0,
    totalPaidOut: 0,
    pendingAmount: 0,
    rejectedAmount: 0,
    cancelledAmount: 0,
    totalAmount: 0,
  });
  // Range picker above the table. Defaults to 'all' so every payout request is
  // listed; 'all' sends no floor at all rather than a very old date.
  const [dateRange, setDateRange] = useState('all');
  // Persisted, because a merchant who hides their balance on a shared screen
  // expects it to stay hidden on the next visit rather than flash on load.
  const [hideBalance, setHideBalance] = useState(() => {
    try {
      return localStorage.getItem('mxl-hide-payout-balance') === '1';
    } catch {
      return false;
    }
  });
  const [copiedId, setCopiedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Skeletons are for the FIRST load only. On a refetch (range, status, page,
  // sort) the current rows and figures stay on screen, dimmed, until the new
  // ones land; swapping them for a skeleton made the page jump on every click.
  const [hasLoadedRequests, setHasLoadedRequests] = useState(false);
  const [hasLoadedStats, setHasLoadedStats] = useState(false);
  // Clicking 15D then 30D quickly fires two requests that can return in either
  // order. Only the latest may write, or a slow 15D reply overwrites 30D.
  const requestsSeq = useRef(0);
  const statsSeq = useRef(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [networks, setNetworks] = useState([]);
  const [tokens, setTokens] = useState([]);




  const [balance, setBalance] = useState({
    availableBalance: 0,
    totalReceived: 0,
    totalWithdrawn: 0,
    pendingPayoutAmount: 0,
    walletAddress: null,
    hasWalletAddress: false,
  });
  const [balanceLoading, setBalanceLoading] = useState(true);

  const [createForm, setCreateForm] = useState({
    amount: '',
    tokenId: null,
    networkId: null,
  });
  // Which Available Assets card is highlighted. Kept separate from createForm
  // because the card stays selected after the modal closes, so the merchant can
  // see which balance they just acted on.
  const [selectedAssetKey, setSelectedAssetKey] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [conversionPreview, setConversionPreview] = useState(null);
  const [conversionLoading, setConversionLoading] = useState(false);

  const [feePreview, setFeePreview] = useState(null);
  const [feePreviewLoading, setFeePreviewLoading] = useState(false);

  const [payoutSettings, setPayoutSettings] = useState({
    minimumWithdrawalAmount: 10,
    payoutFeePercentage: 1,
  });

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Debounced so typing does not fire a request per keystroke.
  const debouncedSearch = useDebounce(searchQuery, 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // One source of truth for the range, so the cards and the list below them can
  // never describe different windows.
  useEffect(() => {
    try {
      localStorage.setItem('mxl-hide-payout-balance', hideBalance ? '1' : '0');
    } catch {
      /* private mode / blocked storage — the toggle still works for this visit */
    }
  }, [hideBalance]);

  const rangeFloorIso = useCallback(() => {
    const days = { '15d': 15, '30d': 30, '90d': 90 }[dateRange];
    if (!days) return null;
    const d = new Date();
    d.setDate(d.getDate() - days);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, [dateRange]);

  const fetchRequests = useCallback(async () => {
    if (!token) return;

    const seq = ++requestsSeq.current;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder,
      });

      params.append('isTestnet', mode === 'test' ? 'true' : 'false');

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      // Server-side: the list is paginated, so filtering here would only ever
      // search the page already on screen.
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }

      const floor = rangeFloorIso();
      if (floor) params.append('dateFrom', floor);

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/my-requests?${params}`);
      const data = await response.json();
      if (seq !== requestsSeq.current) return;

      if (data.success) {
        setRequests(data.data.requests);
        setTotalPages(data.data.pagination.totalPages);
        setTotalItems(data.data.pagination.total);
      }
    } catch (error) {
      if (seq !== requestsSeq.current) return;
      console.error('Failed to fetch payout requests:', error);
      toast.error('Failed to fetch payout requests');
    } finally {
      if (seq === requestsSeq.current) {
        setIsLoading(false);
        setHasLoadedRequests(true);
      }
    }
  }, [token, currentPage, pageSize, statusFilter, sortBy, sortOrder, mode, debouncedSearch, rangeFloorIso]);

  const fetchStats = useCallback(async () => {
    if (!token) return;

    const seq = ++statsSeq.current;
    try {
      const params = new URLSearchParams({
        isTestnet: mode === 'test' ? 'true' : 'false',
      });

      const floor = rangeFloorIso();
      if (floor) params.append('dateFrom', floor);

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/my-stats?${params}`);
      const data = await response.json();
      if (seq !== statsSeq.current) return;

      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch payout stats:', error);
    } finally {
      if (seq === statsSeq.current) setHasLoadedStats(true);
    }
  }, [token, mode, rangeFloorIso]);

  const fetchBalance = useCallback(async () => {
    if (!token) return;

    setBalanceLoading(true);
    try {
      const params = new URLSearchParams({
        isTestnet: mode === 'test' ? 'true' : 'false',
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/my-balance?${params}`);
      const data = await response.json();

      if (data.success) {
        setBalance(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  }, [token, mode]);

  const fetchPayoutOptions = useCallback(async () => {
    if (!token) return;

    try {
      const params = new URLSearchParams({
        isTestnet: mode === 'test' ? 'true' : 'false',
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/options?${params}`);
      const data = await response.json();

      if (data.success) {
        setNetworks(data.data.networks || []);
        setTokens(data.data.tokens || []);
      }
    } catch (error) {
      console.error('Failed to fetch payout options:', error);
    }
  }, [token, mode]);

  const fetchPayoutSettings = useCallback(async () => {
    if (!token) return;

    try {
      const isTestnet = mode === 'test';
      const params = new URLSearchParams({
        isTestnet: isTestnet ? 'true' : 'false',
      });

      console.log(`[Payouts] Fetching payout settings for ${isTestnet ? 'testnet' : 'mainnet'}`);

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/settings?${params}`);
      const data = await response.json();

      if (data.success) {
        console.log(`[Payouts] Settings loaded:`, data.data);
        setPayoutSettings({
          minimumWithdrawalAmount: data.data.minimumWithdrawalAmount,
          payoutFeePercentage: data.data.payoutFeePercentage,
        });
      }
    } catch (error) {
      console.error('Failed to fetch payout settings:', error);
    }
  }, [token, mode]);

  const fetchConversionPreview = useCallback(async (amount, tokenId, networkId) => {
    if (!token || !amount || !tokenId || !networkId) {
      setConversionPreview(null);
      return;
    }

    // Testnet is priced exactly like mainnet: the backend resolves a live market
    // rate for the asset, so a $30 tBNB payout is 30/BNB-USD worth of tBNB, not
    // 30 tBNB. This used to short-circuit test mode with a fabricated
    // conversionRate of 1, which quoted every non-stablecoin testnet payout
    // wrong and did not match the amount the payout was actually created with.
    setConversionLoading(true);
    try {
      const params = new URLSearchParams({
        amount,
        tokenId,
        networkId,
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/preview-conversion?${params}`);
      const data = await response.json();

      // Clear on failure rather than leaving a stale quote on screen — an
      // unpriceable token has no honest number to show.
      setConversionPreview(data.success ? data.data : null);
    } catch (error) {
      console.error('Failed to fetch conversion preview:', error);
      setConversionPreview(null);
    } finally {
      setConversionLoading(false);
    }
  }, [token]);

  const fetchFeePreview = useCallback(async (amount, asset) => {
    if (!token || !amount || !asset) {
      setFeePreview(null);
      return;
    }

    setFeePreviewLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/fee-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parseFloat(amount), asset }),
      });
      const data = await response.json();

      if (data.success) {
        setFeePreview(data.data);
      } else {
        setFeePreview(null);
      }
    } catch (error) {
      console.error('Failed to fetch fee preview:', error);
      setFeePreview(null);
    } finally {
      setFeePreviewLoading(false);
    }
  }, [token]);

  const debouncedAmount = useDebounce(createForm.amount, 500);


useEffect(() => {
  if (showCreateModal) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }

  return () => {
    document.body.style.overflow = "";
  };
}, [showCreateModal]);

  // Separate effects, so each fetch reruns only when its own inputs change.
  // As one effect, a range or page change also refetched the balance, options
  // and settings: the balance card and Available assets blinked through their
  // loading state on every filter click although none of them depend on it.
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchBalance();
    fetchPayoutOptions();
    fetchPayoutSettings();
  }, [fetchBalance, fetchPayoutOptions, fetchPayoutSettings]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (createForm.amount && createForm.tokenId && createForm.networkId) {
        fetchConversionPreview(createForm.amount, createForm.tokenId, createForm.networkId);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [createForm.amount, createForm.tokenId, createForm.networkId, fetchConversionPreview]);

  useEffect(() => {
    const selectedToken = tokens.find((t) => t.id === createForm.tokenId);
    if (debouncedAmount && parseFloat(debouncedAmount) > 0 && selectedToken) {
      fetchFeePreview(debouncedAmount, selectedToken.symbol);
    } else {
      setFeePreview(null);
    }
  }, [debouncedAmount, createForm.tokenId, tokens, fetchFeePreview]);

  // `autoConv` is kept only for the read-only status strip further down. It no
  // longer resolves a target network/token, because the payout pickers are not
  // constrained by it: what a merchant may withdraw is what they hold.

  // The payout pickers offer only what the merchant can actually withdraw.
  //
  // They used to list every network and token for the mode, so a merchant
  // holding USDT on BSC-Testnet could select Sepolia — where they hold nothing —
  // and raise a request that reserved against an empty balance. The backend now
  // refuses that, but the dropdown should never have offered it: the options a
  // form presents are a promise that they work.
  //
  // Derived from the same balance rows as the Available Assets cards, so the two
  // can never disagree.
  const heldNetworkIds = new Set((balance.balances || []).map((r) => r.networkId));
  const heldSymbolsByNetwork = (balance.balances || []).reduce((map, r) => {
    const set = map.get(r.networkId) || new Set();
    set.add((r.token || '').toUpperCase());
    map.set(r.networkId, set);
    return map;
  }, new Map());

  // The balance row for the pair currently selected in the form.
  //
  // The modal used to show, cap and validate against balance.availableBalance —
  // the merchant's TOTAL across every chain. That is the wrong ceiling for a
  // payout, which settles from one asset on one chain: with $100 on Sepolia and
  // $1,230 on BSC-Testnet, a $200 Sepolia request passed every check in the form
  // and was only refused by the server.
  const selectedTokenRow = tokens.find((t) => t.id === createForm.tokenId) || null;
  const selectedAssetBalance =
    createForm.networkId && selectedTokenRow
      ? (balance.balances || []).find(
          (r) =>
            r.networkId === createForm.networkId &&
            (r.token || '').toUpperCase() === (selectedTokenRow.symbol || '').toUpperCase()
        ) || null
      : null;

  // Until a pair is chosen there is nothing more specific to offer, so the
  // aggregate stands in.
  const payoutCeilingUsd = selectedAssetBalance
    ? selectedAssetBalance.availableUsd
    : balance.availableBalance;

  // Networks available for the payout picker, scoped to the active test/live mode.
  const filteredNetworks = networks.filter((n) => (mode === 'test' ? n.isTestnet : !n.isTestnet));

  // No auto-conversion pre-fill here on purpose.
  //
  // A payout is constrained by the balance the merchant holds, not by their
  // conversion target, so the pickers stay open and the Available Assets cards
  // above are what fills the form in. This effect also used to overwrite an
  // explicit card selection the moment the modal opened.

  const handleOpenCreateModal = async () => {
    await fetchPayoutSettings();
    setShowCreateModal(true);
    // P5: nudge Free merchants toward Premium when they start a payout. Savings
    // estimate = the extra fee they'd avoid on a full withdrawal at 1% vs feePct.
    if (feePct != null && feePct > 1) {
      const savings = ((feePct - 1) / 100) * (available || 0);
      upsell.maybeShow('payout_modal', {
        savings,
        context: 'Premium merchants pay a 1% fee and get priority payouts.',
      });
    }
  };

  // Selecting a balance card opens the payout form already pointed at that
  // exact (network, asset). The merchant picked a specific balance; making them
  // re-choose the network and token from scratch invites picking a pair they do
  // not actually hold.
  const handleSelectAsset = async (row) => {
    setSelectedAssetKey(assetKey(row));

    // The cards come from the balance endpoint and the pickers from the payout
    // options endpoint, so the token has to be resolved across the two by
    // (network, symbol) — an id is not shared between them.
    const token = tokens.find(
      (t) =>
        t.networkId === row.networkId &&
        (t.symbol || '').toUpperCase() === (row.token || '').toUpperCase()
    );

    if (!token) {
      // The balance exists but is not payable through the current option set —
      // a delisted token, or a network disabled since the payment arrived.
      toast.error(
        `${row.token} on ${row.chain} can't be paid out right now. Contact support if this looks wrong.`
      );
      return;
    }

    setCreateForm((prev) => ({ ...prev, tokenId: token.id, networkId: row.networkId }));
    await handleOpenCreateModal();
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({
      amount: '',
      tokenId: null,
      networkId: null,
    });
    setConversionPreview(null);
    setFeePreview(null);
  };

  // ---- Drawer exit animation -------------------------------------------
  // The drawer used to vanish between two frames: every close path called
  // handleCloseCreateModal, which flips showCreateModal and unmounts the panel
  // immediately, so there was nothing left on screen to animate. That is what
  // made closing feel abrupt next to the fade-in.
  //
  // requestCloseCreateModal marks the panel as leaving and lets CSS fade it
  // out; handleCloseCreateModal then runs unchanged once the animation is done.
  // The reset is only DEFERRED, never altered.
  const [drawerClosing, setDrawerClosing] = useState(false);
  const closeBailRef = useRef(null);

  // Plain functions, not useCallback: they close over handleCloseCreateModal,
  // which is re-created every render, so memoising them would either go stale
  // or trip exhaustive-deps. Nothing here is passed to a memoised child.
  const finishCloseCreateModal = () => {
    if (closeBailRef.current) {
      clearTimeout(closeBailRef.current);
      closeBailRef.current = null;
    }
    setDrawerClosing(false);
    handleCloseCreateModal();
  };

  const requestCloseCreateModal = () => {
    // Honour the OS setting, and skip the wait when the animation would not run
    // anyway (the CSS below disables it under the same query).
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      handleCloseCreateModal();
      return;
    }
    setDrawerClosing((already) => {
      if (already) return already;
      // animationend is not guaranteed to fire (a backgrounded tab, an
      // interrupted animation), so never leave the panel stuck mid-exit.
      closeBailRef.current = setTimeout(finishCloseCreateModal, 400);
      return true;
    });
  };

  useEffect(() => () => {
    if (closeBailRef.current) clearTimeout(closeBailRef.current);
  }, []);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setShowFilterDropdown(false);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();

    if (!createForm.amount || !createForm.tokenId || !createForm.networkId) {
      toast.error('Please fill in all required fields');
      return;
    }

    const requestedAmount = parseFloat(createForm.amount);
    if (requestedAmount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    if (requestedAmount < payoutSettings.minimumWithdrawalAmount) {
      toast.error(`Minimum withdrawal amount is $${formatUSD(payoutSettings.minimumWithdrawalAmount)}`);
      return;
    }

    if (requestedAmount > payoutCeilingUsd) {
      toast.error(
        selectedAssetBalance
          ? `Insufficient ${selectedAssetBalance.token} balance on ${selectedAssetBalance.chain}. Available: $${formatUSD(payoutCeilingUsd)}`
          : `Insufficient balance. Available: $${formatUSD(payoutCeilingUsd)}`
      );
      return;
    }

    if (!balance.hasWalletAddress) {
      toast.error('Please set your wallet address in Settings before requesting a payout');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/payouts`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          amount: requestedAmount,
          tokenId: createForm.tokenId,
          networkId: createForm.networkId,
          isTestnet: mode === 'test',
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Payout request created successfully');
        requestCloseCreateModal();
        fetchRequests();
        fetchStats();
        fetchBalance();
      } else {
        toast.error(data.message || 'Failed to create payout request');
      }
    } catch (error) {
      console.error('Failed to create payout request:', error);
      toast.error('Failed to create payout request');
    } finally {
      setCreateLoading(false);
    }
  };

  // Open the full payout detail page, seeding it with the already-loaded row for
  // an instant first paint (the page refetches for the canonical record). Cancel
  // now lives on that page.
  const openPayout = (request) => {
    navigate(`/dashboard/payouts/${request.requestId}`, { state: { request } });
  };

  // BUG-09: export the merchant's payout requests as CSV (server-side, all rows
  // matching the current status filter). Gated by the csv_pdf_export plan feature
  // (403 → prompt to upgrade), mirroring the Invoices export.
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const params = new URLSearchParams({ sortBy, sortOrder });
      params.append('isTestnet', mode === 'test' ? 'true' : 'false');
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/payouts/export?${params.toString()}`);
      if (response.status === 403) {
        toast.error('CSV export is a Premium feature');
        return;
      }
      if (!response.ok) {
        toast.error('Failed to export payouts');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payouts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export payouts failed:', error);
      toast.error('Failed to export payouts');
    } finally {
      setIsExporting(false);
    }
  };

  const truncateAddress = (address) => {
    if (!address) return '-';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const statusTabs = [
    { id: 'all', label: 'All', count: stats.totalRequests },
    { id: 'pending', label: 'Pending', count: stats.pendingRequests },
    { id: 'completed', label: 'Completed', count: stats.completedRequests },
  ];

  // ---- Plan-aware hero / fee figures (real data; "—" when unavailable) ----
  const planKey = plan?.planKey || null;
  const isFreePlan = planKey === 'free';
  const feePct = plan?.feePct ?? null; // e.g. 2 (Free) / 1 (Premium)
  const planLabel = (key) =>
    key ? `${key.charAt(0).toUpperCase()}${key.slice(1)} plan` : '';
  // The percentage the server actually priced this preview with, falling back to
  // the plan's own value on an older API response.
  const feeRowPct = feePreview?.feePct ?? feePct;
  // Per-row fee/net computed from the merchant's effective fee % (rows omit fee_amount/net_amount).
  // Mirrors the backend rule in services/feeCalculator.js — the authority:
  //   platform fee = max(amount * feePct/100, minimum for the asset)
  // Without the floor these estimates understate the real charge on small
  // payouts (a $30 payout costs $1.00 on Free, not $0.60).
  const feeMinimum = (symbol) => {
    const mins = plan?.minimums;
    if (!mins) return 0;
    const min = String(symbol ?? '').toUpperCase() === 'ETH' ? mins.eth : mins.general;
    return min != null ? Number(min) : 0;
  };
  const rowFee = (amount, symbol) =>
    feePct != null ? Math.max((Number(amount) * feePct) / 100, feeMinimum(symbol)) : null;
  const rowNet = (amount, symbol) =>
    feePct != null ? Number(amount) - rowFee(amount, symbol) : null;

  const available = balance.availableBalance || 0;
  const estFeeOnFull = feePct != null ? rowFee(available) : null;
  const netOnFull = estFeeOnFull != null ? available - estFeeOnFull : null;
  // Annual savings on Premium = (current fee % - Premium 1%) applied to lifetime paid-out volume.
  const PREMIUM_FEE_PCT = 1;
  const annualPremiumSavings =
    isFreePlan && feePct != null && stats.totalPaidOut > 0
      ? (stats.totalPaidOut * (feePct - PREMIUM_FEE_PCT)) / 100
      : null;


  // An admin rejection is not a failed blockchain transaction, so 'rejected' is
  // passed through and renders its own red "Rejected" pill; 'failed' stays
  // reserved for payouts that failed on chain. The admin's rejection reason is
  // surfaced as the badge detail, truncated rather than replaced by a canned
  // label (a long reason used to be rendered as the literal "Network fee").
  const statusDetail = (request) => {
    const reason = request.rejectionReason || request.rejection_reason;
    if (request.status !== 'rejected' || !reason) return undefined;
    return reason.length > 18 ? `${reason.slice(0, 18)}…` : reason;
  };

  // Coins in the balance card's artwork: the merchant's three largest holdings
  // by USD value, deduped by symbol so the same token on two chains does not
  // render twice.
  //
  // Padded out to three from a default set when they hold fewer. The artwork is
  // a cluster — one lone coin in the corner of a large card reads as a loading
  // state rather than a decoration — and an account holding a single asset is
  // the normal case on testnet and on a new account. The padding is decorative
  // only: nothing here is a balance, a holding or a supported-asset claim.
  const heroAssets = useMemo(() => {
    const seen = new Set();
    const held = (balance.balances || [])
      .slice()
      .sort((a, b) => (b.availableUsd || 0) - (a.availableUsd || 0))
      .filter((r) => {
        const sym = String(r.token || '').toUpperCase();
        if (!sym || seen.has(sym)) return false;
        seen.add(sym);
        return true;
      })
      .slice(0, 3)
      .map((r) => String(r.token).toUpperCase());

    const filler = HERO_FALLBACK.filter((sym) => !seen.has(sym));
    return [...held, ...filler].slice(0, 3);
  }, [balance.balances]);

  const RANGES = [
    { key: 'all', label: 'All time' },
    { key: '15d', label: 'Last 15 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
  ];
  const rangeLabel = RANGES.find((r) => r.key === dateRange)?.label || 'All time';

  // A share is only meaningful against a non-zero total, and "0%" on an empty
  // account reads as a real measurement rather than as no data.
  const shareOfTotal = (amount) => {
    const total = Number(stats.totalAmount) || 0;
    const part = Number(amount) || 0;
    if (!total) return null;
    const pct = (part / total) * 100;
    if (pct > 0 && pct < 1) return '<1%';
    return `${Math.round(pct)}%`;
  };

  const copyHash = async (e, request) => {
    e.stopPropagation();
    const value = request.txHash || request.requestId;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(request.requestId);
      setTimeout(() => setCopiedId((c) => (c === request.requestId ? null : c)), 1400);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  // The mockup puts the date over the time. Both come off the same timestamp.
  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const tabs = statusTabs.map((t) => ({ key: t.id, label: t.label, count: formatCount(t.count) }));
  // Admin-rejected payouts get their own tab. The key is the real status, so the
  // filter passes straight through to the API with no remapping.
  tabs.push({ key: 'rejected', label: 'Rejected', count: formatCount(stats.rejectedRequests || 0) });
  // Cancelled is the most common status on this table, and until it had a tab
  // those rows appeared under "All" with no way to filter to them.
  tabs.push({ key: 'cancelled', label: 'Cancelled', count: formatCount(stats.cancelledRequests || 0) });

  return (
    <div className="ent ent-page payouts-page">
      <PageHeaderPro
        eyebrow={
          <>
            <Wallet size={14} aria-hidden="true" />
            Payouts
          </>
        }
        title="Payout Requests"
        subtitle="Manage your crypto payouts, track status, and view transaction details."
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={balanceLoading ? 'Updating…' : 'Updated just now'}
        actions={
          <>
            <button
              className="ent-btn ent-btn-secondary"
              type="button"
              onClick={() => navigate('/dashboard/settings')}
            >
              <SettingsIcon size={16} />
              Settings
            </button>
            <button className="ent-btn ent-btn-primary" type="button" onClick={handleOpenCreateModal}>
              <Send size={16} />
              Request Payout
            </button>
          </>
        }
      />

      {/* Balance + auto-conversion. Two cards on one row: the violet one is the
          money, the white one is the setting that decides which asset it can
          leave in. */}
      <div className="po-top">
        <div className="po-bal">
          <div className="po-bal__art" aria-hidden="true">
            {/* The merchant's largest holdings lead the cluster, topped up from
                a default set when they hold fewer than three. Decoration only —
                see heroAssets for why it is padded rather than left short. */}
            <span className="po-bal__rings" />
            {heroAssets.map((sym, i) => (
              <span
                key={`${sym}-${i}`}
                className={`po-bal__coin po-bal__coin-${i + 1}`}
                // Each coin's own glow, so the cluster picks up the token
                // colours rather than sitting in one flat violet haze.
                style={{ '--coin-glow': tokenColor(sym) }}
              >
                <span className="po-bal__coin-face">
                  <AssetSpot symbol={sym} />
                </span>
              </span>
            ))}
          </div>

          <div className="po-bal__head">
            <span className="po-bal__label">Total Available Balance</span>
            <button
              type="button"
              className="po-bal__eye"
              onClick={() => setHideBalance((v) => !v)}
              aria-pressed={hideBalance}
              title={hideBalance ? 'Show balance' : 'Hide balance'}
              aria-label={hideBalance ? 'Show balance' : 'Hide balance'}
            >
              {hideBalance ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          </div>

          <div className="po-bal__figure">
            {balanceLoading ? (
              <span className="po-bal__amt">$—</span>
            ) : hideBalance ? (
              <span className="po-bal__amt po-bal__amt--hidden">••••••</span>
            ) : (
              <span className="po-bal__amt">${formatUSD(available)}</span>
            )}
          </div>

          <div className="po-bal__stats">
            <div className="po-bal__cell">
              <span className="po-bal__ico"><Wallet size={15} /></span>
              <span className="po-bal__cellbody">
                <span className="po-bal__k">Plan</span>
                <span className="po-bal__v">
                  {planKey ? `${planKey.charAt(0).toUpperCase()}${planKey.slice(1)}` : '—'}
                  {feePct != null && <span className="po-bal__fee"> · {feePct}% fee</span>}
                </span>
              </span>
            </div>
            <span className="po-bal__div" />
            <div className="po-bal__cell">
              <span className="po-bal__ico"><Percent size={15} /></span>
              <span className="po-bal__cellbody">
                <span className="po-bal__k">Est. fee on full</span>
                <span className="po-bal__v">
                  {hideBalance ? '••••' : estFeeOnFull != null ? `$${formatUSD(estFeeOnFull)}` : '—'}
                </span>
              </span>
            </div>
            <span className="po-bal__div" />
            <div className="po-bal__cell">
              <span className="po-bal__ico"><Send size={15} /></span>
              <span className="po-bal__cellbody">
                <span className="po-bal__k">Net you&apos;d receive</span>
                <span className="po-bal__v po-bal__v--net">
                  {hideBalance ? '••••' : netOnFull != null ? `$${formatUSD(netOnFull)}` : '—'}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="po-autoconv">
          <div className="po-autoconv__icon">
            <Repeat size={20} />
          </div>
          <div className="po-autoconv__body">
            <span className="po-autoconv__title">
              Auto-conversion
              <span className={`po-autoconv__state${autoConv?.enabled ? ' is-on' : ''}`}>
                {autoConv?.enabled ? 'On' : 'Off'}
              </span>
              {hasPriority && <span className="priority-chip">Priority payouts · active</span>}
            </span>
            {autoConv?.enabled ? (
              <span className="po-autoconv__desc">
                Converting incoming payments to <strong>{autoConv.target_asset}</strong>
                {autoConv.settlement_mode === 'specific_chain' && autoConv.target_chain
                  ? <> on <strong>{autoConv.target_chain}</strong></>
                  : ' on the same chain'}
                {' '}— so payouts settle in one asset.
              </span>
            ) : (
              <span className="po-autoconv__desc">
                Turn it on to auto-convert every incoming payment into one settlement asset and skip manual swaps before payout.
              </span>
            )}
            {annualPremiumSavings != null && annualPremiumSavings > 0 && (
              <span className="po-nudge">
                <ArrowUpRight size={14} />
                Save ${formatUSD(annualPremiumSavings)}/yr on Premium (1% fee)
              </span>
            )}
          </div>
          <button
            className="po-autoconv__cta"
            type="button"
            onClick={() => navigate('/dashboard/auto-conversion')}
          >
            {autoConv?.enabled ? 'Manage' : 'Set up'}
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Totals for the selected range, mirroring the tabs below exactly: the
          Total card is every request (the "All" tab), and each status card is
          that status' share of it.

          The three shares do NOT have to reach 100%. A payout can also be
          cancelled, failed, approved or processing, and those rows are counted
          in the total — as they are in the "All" tab — but have no card of
          their own. An account holding one cancelled payout and nothing else
          therefore reads Total $100 with three zeroes under it, which is
          accurate but easy to misread as a bug. */}
      <StatCardGrid>
        <StatCard
          tone="brand"
          icon={Wallet}
          label="Total Payouts"
          value={`$${formatUSD(stats.totalAmount || 0)}`}
          sub={`${formatCount(stats.totalRequests || 0)} ${
            (stats.totalRequests || 0) === 1 ? 'request' : 'requests'
          } · ${rangeLabel.toLowerCase()}`}
          loading={!hasLoadedStats}
          onAction={() => handleStatusFilter('all')}
          actionLabel="Show all payout requests"
        />
        <StatCard
          tone="ok"
          icon={CheckCircle2}
          label="Completed"
          value={`$${formatUSD(stats.totalPaidOut || 0)}`}
          chip={shareOfTotal(stats.totalPaidOut)}
          sub="of total"
          loading={!hasLoadedStats}
          onAction={() => handleStatusFilter('completed')}
          actionLabel="Show completed payouts"
        />
        <StatCard
          tone="warn"
          icon={Clock}
          label="Pending"
          value={`$${formatUSD(balance.pendingPayoutAmount || stats.pendingAmount || 0)}`}
          chip={shareOfTotal(stats.pendingAmount)}
          sub="of total"
          loading={!hasLoadedStats}
          onAction={() => handleStatusFilter('pending')}
          actionLabel="Show pending payouts"
        />
        <StatCard
          tone="err"
          icon={XCircle}
          label="Rejected"
          value={`$${formatUSD(stats.rejectedAmount || 0)}`}
          chip={shareOfTotal(stats.rejectedAmount)}
          sub="of total"
          loading={!hasLoadedStats}
          onAction={() => handleStatusFilter('rejected')}
          actionLabel="Show rejected payouts"
        />
      </StatCardGrid>

      {/* What can actually be withdrawn, per (network, asset). Sits above the
          history because choosing a balance is the first step of a payout, and
          the merchant should not have to guess which pairs they hold. */}
      <AvailableAssets
        balances={balance.balances || []}
        loading={balanceLoading}
        selectedKey={selectedAssetKey}
        onSelect={handleSelectAsset}
        // So a balance too small to withdraw says so on the card, rather than
        // failing after the merchant has filled in the form.
        minimumUsd={payoutSettings.minimumWithdrawalAmount || 0}
      />

      {/* Toolbar, table and pager are one card: the tabs filter the rows
          directly beneath them, and splitting them across two surfaces broke
          that relationship visually. */}
      <div className="po-panel">
      <FilterTabs
        variant="underline"
        tabs={tabs}
        active={statusFilter}
        onChange={handleStatusFilter}
        right={
          <div className="po-toolbar">
            <EntSearch
              value={searchQuery}
              onChange={(v) => {
                setSearchQuery(v);
                setCurrentPage(1);
              }}
              placeholder="Search by request #, tx hash or asset…"
            />
            <label className="po-range">
              <CalendarDays size={15} aria-hidden="true" />
              <select
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setCurrentPage(1);
                }}
                aria-label="Date range"
              >
                {RANGES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} aria-hidden="true" />
            </label>
            <button
              type="button"
              className="po-iconbtn"
              onClick={handleExport}
              disabled={isExporting}
              title="Export these results as CSV"
              aria-label="Export these results as CSV"
            >
              <Download size={16} />
            </button>
          </div>
        }
      />

      <div
        className={`pro-table-wrap po-table-wrap ${isLoading && hasLoadedRequests ? 'is-refreshing' : ''}`}
        aria-busy={isLoading}
      >
        {isLoading && !hasLoadedRequests ? (
          <TableSkeleton rows={pageSize} cols={8} />
        ) : requests.length === 0 ? (
          <div className="empty-state">
            <Send size={48} />
            {debouncedSearch ? (
              <>
                <p>No payouts match “{debouncedSearch}”</p>
                <span>Try a different request #, destination address or asset.</span>
                <button
                  className="ent-btn ent-btn-secondary mt-4"
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <p>No payout requests yet</p>
                <span>Create your first payout request to withdraw funds</span>
                <button className="ent-btn ent-btn-primary mt-4" onClick={handleOpenCreateModal}>
                  Request payout
                </button>
              </>
            )}
          </div>
        ) : (
          <table className="pro-table">
            <thead>
              <tr>
                <th>ID / TX HASH</th>
                <th>DESTINATION</th>
                <th
                  className="amount-cell"
                  onClick={() => handleSort('amount')}
                  style={{ cursor: 'pointer' }}
                >
                  AMOUNT
                </th>
                <th className="amount-cell">FEE</th>
                <th className="amount-cell">NET AMOUNT</th>
                <th>STATUS</th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                  DATE
                </th>
                <th className="col-kebab">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const fee = rowFee(request.amount, request.tokenSymbol);
                const net = rowNet(request.amount, request.tokenSymbol);
                return (
                  <tr key={request.id} onClick={() => openPayout(request)} style={{ cursor: 'pointer' }}>
                    <td data-label="ID / TX HASH">
                      <div className="po-idcell">
                        <span className="po-idcell__top">
                          <span className="po-idcell__id">{request.requestId}</span>
                          {request.isTestnet && <span className="testnet-badge">Testnet</span>}
                          {request.priority && <span className="priority-badge">Priority</span>}
                        </span>
                        {/* Until a payout settles there is no hash to show. The
                            line says so in plain text rather than collapsing,
                            which would change the row height mid-table — and it
                            is deliberately not monospaced, because a sentence set
                            in a hash font reads as data the merchant could copy. */}
                        {request.txHash ? (
                          <span className="po-idcell__hash">
                            <span className="mono">{truncateHash(request.txHash, 6, 4)}</span>
                            <button
                              type="button"
                              className="po-copy"
                              onClick={(e) => copyHash(e, request)}
                              title="Copy transaction hash"
                              aria-label={`Copy transaction hash for ${request.requestId}`}
                            >
                              {copiedId === request.requestId ? (
                                <Check size={13} />
                              ) : (
                                <Copy size={13} />
                              )}
                            </button>
                          </span>
                        ) : (
                          <span className="po-idcell__hash po-idcell__hash--empty">
                            Not settled yet
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="DESTINATION">
                      {/* Asset over address, as one two-line block. The shared
                          AssetBadge put the chain name and the address on the
                          same baseline, and because the chain reads far wider
                          than the symbol above it, the address ended up stranded
                          mid-column with a hole beside it. */}
                      <div className="po-dest">
                        <TokenChainLogo
                          symbol={request.tokenSymbol}
                          chain={request.networkName}
                          size={32}
                        />
                        <span className="po-dest__text">
                          <span className="po-dest__asset">
                            {request.tokenSymbol || 'Wallet'}
                            {request.networkName ? (
                              <span className="po-dest__chain">{request.networkName}</span>
                            ) : null}
                          </span>
                          <span className="po-dest__addr mono">
                            {request.destinationAddress
                              ? truncateHash(request.destinationAddress, 6, 4)
                              : '—'}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="amount-cell" data-label="AMOUNT">
                      <span className="amount-cell__main">${formatUSD(request.amount)}</span>
                      {request.tokenAmount && (
                        <span className="amount-cell__sub">
                          {formatCrypto(parseFloat(request.tokenAmount))} {request.tokenSymbol}
                        </span>
                      )}
                    </td>
                    <td className="amount-cell" data-label="FEE">
                      <span className="amount-cell__main" style={{ fontWeight: 400 }}>
                        {fee != null ? `$${formatUSD(fee)}` : '—'}
                      </span>
                    </td>
                    <td className="amount-cell" data-label="NET AMOUNT">
                      <span className="amount-cell__main">{net != null ? `$${formatUSD(net)}` : '—'}</span>
                    </td>
                    <td data-label="STATUS">
                      <StatusBadge
                        status={request.status}
                        detail={statusDetail(request)}
                        icon
                      />
                    </td>
                    <td data-label="DATE">
                      <div className="cell-2">
                        <span className="cell-2__main po-date">{formatDate(request.createdAt)}</span>
                        <span className="cell-2__sub">{formatTime(request.createdAt)}</span>
                      </div>
                    </td>
                    <td className="col-kebab">
                      <button
                        className="row-kebab"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPayout(request);
                        }}
                        aria-label="Row actions"
                      >
                        <MoreVertical size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {requests.length > 0 && (
        <Pager
          page={currentPage}
          pageCount={totalPages}
          total={totalItems}
          pageSize={pageSize}
          onPage={setCurrentPage}
        />
      )}
      </div>

      {showCreateModal && (
        <>
          {/*
            `open` is not decorative. index.css and responsive/nav.css both ship
            .drawer-overlay as opacity:0 + visibility:hidden at every width,
            revealed only by .open -- the mobile-nav drawer contract that
            Sidebar.jsx and all four scrims in PaymentLinks.jsx opt into.
            Without it this scrim was invisible AND unclickable, because
            visibility:hidden also removes an element from hit testing, so
            neither the dim nor the blur could ever show.
          */}
          <div
            className={`drawer-overlay open create-drawer-overlay ${
              drawerClosing ? 'is-closing' : ''
            }`}
            onClick={requestCloseCreateModal}
          />
          <div
            className={`create-drawer ${drawerClosing ? 'is-closing' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-payout-title"
            onAnimationEnd={(e) => {
              // Children animate too; only the panel's own exit ends the close.
              if (e.target !== e.currentTarget) return;
              if (drawerClosing) finishCloseCreateModal();
            }}
          >
            {/* Matches the Create Invoice drawer header: close on the left,
                then a tinted icon tile, then the title. Uses the same
                .drawer-header / .drawer-title-section / .drawer-icon-wrapper
                classes so the two headers stay in step.

                The icon is the imported lucide <Send>, not an inlined SVG: a
                pasted-from-devtools SVG carries hyphenated attributes
                (stroke-width, stroke-linecap) that JSX does not accept, and it
                would also have shown the file-text glyph, which belongs to
                invoices. */}
            <div className="drawer-header">
              <div className="drawer-header-left">
                <button
                  className="close-btn"
                  onClick={requestCloseCreateModal}
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
                <div className="drawer-title-section">
                  <div className="drawer-icon-wrapper">
                    <Send size={18} />
                  </div>
                  <div>
                    {/* id is referenced by the panel's aria-labelledby. */}
                    <h2 id="create-payout-title">Request Payout</h2>
                  </div>
                </div>
              </div>
            </div>
            <form onSubmit={handleCreateRequest}>
              <div className="modal-content">
                <div className="modal-balance-card">
                  <div className="modal-balance-info">
                    <span className="modal-balance-label">
                      {selectedAssetBalance
                        ? `Available · ${selectedAssetBalance.token} on ${selectedAssetBalance.chain}`
                        : 'Available Balance'}
                    </span>
                    <span className="modal-balance-value">${formatUSD(payoutCeilingUsd)}</span>
                    {selectedAssetBalance && (
                      // The token figure is the one that actually moves; USD is
                      // a valuation of it.
                      <span className="modal-balance-sub">
                        {formatCrypto(selectedAssetBalance.available)} {selectedAssetBalance.token}
                      </span>
                    )}
                  </div>
                  <div className="modal-wallet-info">
                    <Wallet size={14} />
                    <span>
                      {balance.hasWalletAddress
                        ? truncateAddress(balance.walletAddress)
                        : 'No wallet set'}
                    </span>
                  </div>
                </div>

                {!balance.hasWalletAddress && (
                  <div className="warning-box payout-wallet-warning">
                    <AlertCircle size={16} />
                    <span>Please set your wallet address in Settings before requesting a payout.</span>
                    <button
                      type="button"
                      className="ent-btn ent-btn-primary payout-wallet-warning__btn"
                      onClick={() => {
                        handleCloseCreateModal();
                        navigate('/dashboard/settings?tab=wallet');
                      }}
                    >
                      <Wallet size={16} />
                      Add wallet
                    </button>
                  </div>
                )}

                <div className="form-group">
                  <label>Amount (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={payoutCeilingUsd}
                    value={createForm.amount}
                    onChange={(e) => setCreateForm({ ...createForm, amount: e.target.value })}
                    placeholder={`Min: $${payoutSettings.minimumWithdrawalAmount}`}
                    required
                  />
                  {createForm.amount && parseFloat(createForm.amount) < payoutSettings.minimumWithdrawalAmount && (
                    <span className="form-error">Minimum withdrawal: ${payoutSettings.minimumWithdrawalAmount}</span>
                  )}
                  {createForm.amount && parseFloat(createForm.amount) > payoutCeilingUsd && (
                    <span className="form-error">
                      {selectedAssetBalance
                        ? `Exceeds your ${selectedAssetBalance.token} balance on ${selectedAssetBalance.chain} ($${formatUSD(payoutCeilingUsd)} available)`
                        : 'Amount exceeds available balance'}
                    </span>
                  )}
                </div>

                {createForm.amount && parseFloat(createForm.amount) > 0 && createForm.tokenId && feePreview && (
                  <div className="fee-calculation-box">
                    <div className="fee-calculation-header">
                      <span>Fee Breakdown</span>
                      {feePreview.planKey === 'premium' && (
                        <span className="premium-pricing-badge">Premium pricing</span>
                      )}
                      {feePreviewLoading && <span className="conversion-loading">Updating...</span>}
                    </div>
                    <div className="fee-calculation-rows">
                      <div className="fee-row">
                        <span>Requested Amount</span>
                        <span>${formatUSD(parseFloat(createForm.amount))}</span>
                      </div>
                      <div className="fee-row fee-deduction">
                        <span>
                          Processing Fee
                          {/* On a small withdrawal the plan's minimum fee beats the
                              percentage, so say that outright — labelling a $1.00
                              minimum as "2%" of $30 reads as a broken calculation. */}
                          {feePreview.minimumApplied ? (
                            <>
                              <span className="fee-row__meta">
                                {' '}· ${formatUSD(feePreview.minimumUsd)} minimum
                              </span>
                              <span className="fee-row__note">
                                {planLabel(feePreview.planKey)}
                                {feeRowPct != null && `, ${feeRowPct}% = $${formatUSD(feePreview.pctFeeUsd)}`}
                              </span>
                            </>
                          ) : (
                            feeRowPct != null && (
                              <span className="fee-row__meta">
                                {' '}· {feeRowPct}%
                                {feePreview.planKey ? ` (${planLabel(feePreview.planKey)})` : ''}
                              </span>
                            )
                          )}
                        </span>
                        <span>-${formatUSD(feePreview.feeUsd)}</span>
                      </div>
                      <div className="fee-row fee-deduction">
                        <span>Network Fee</span>
                        <span>-${formatUSD(feePreview.networkFeeUsd)}</span>
                      </div>
                      <div className="fee-row net-amount">
                        <span>You Will Receive</span>
                        <span>${formatUSD(feePreview.netUsd)}</span>
                      </div>
                    </div>
                    {feePreview.blocked && (
                      <div className="fee-blocked-reason">
                        <AlertCircle size={14} />
                        <span>{feePreview.reason}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Network *</label>
                    <AssetSelect
                      kind="chain"
                      value={createForm.networkId}
                      onChange={(v) => setCreateForm({
                        ...createForm,
                        networkId: v ? parseInt(v, 10) : null,
                        tokenId: null
                      })}
                      placeholder="Select network"
                      options={filteredNetworks
                        .filter((network) => heldNetworkIds.has(network.id))
                        .map((network) => ({
                          value: network.id,
                          label: network.name,
                          chain: network.slug || network.name,
                          logoUrl: network.logoUrl,
                        }))}
                    />
                  </div>

                  <div className="form-group">
                    <label>Token *</label>
                    <AssetSelect
                      kind="token"
                      value={createForm.tokenId}
                      onChange={(v) => setCreateForm({ ...createForm, tokenId: v ? parseInt(v, 10) : null })}
                      disabled={!createForm.networkId}
                      placeholder={createForm.networkId ? 'Select token' : 'Select network first'}
                      options={tokens
                        .filter(
                          (token) =>
                            token.networkId === createForm.networkId &&
                            (heldSymbolsByNetwork.get(createForm.networkId) || new Set()).has(
                              (token.symbol || '').toUpperCase()
                            )
                        )
                        .map((t) => ({
                          value: t.id,
                          label: t.symbol,
                          sublabel: t.name,
                          symbol: t.symbol,
                          logoUrl: t.logoUrl,
                        }))}
                    />
                  </div>
                </div>

                {conversionPreview && createForm.amount && parseFloat(createForm.amount) >= payoutSettings.minimumWithdrawalAmount && (
                  <div className="conversion-preview">
                    <div className="conversion-header">
                      <span>Token Amount (after fee)</span>
                      {conversionLoading && <span className="conversion-loading">Updating...</span>}
                    </div>
                    <div className="conversion-amount">
                      {(() => {
                        // Price the SAME net the breakdown shows and the backend
                        // stores as net_amount. This used to recompute the net
                        // from the system fee %, which ignored the plan fee and
                        // its minimum, so a $30 payout read "29.4 USDT" beside
                        // "You Will Receive $29.00".
                        const gross = parseFloat(createForm.amount);
                        const netUsd =
                          feePreview?.netUsd != null
                            ? feePreview.netUsd
                            : gross - (rowFee(gross, conversionPreview.tokenSymbol) ?? 0);
                        // conversionRate is USD per token (the backend does
                        // tokenAmount = usd / rate), on testnet as well as
                        // mainnet — so divide, and never assume 1 USD = 1 token.
                        const rate = Number(conversionPreview.conversionRate) || 0;
                        const tokenAmount = rate > 0 ? netUsd / rate : null;
                        return (
                          <>
                            <span className="conversion-token-amount">
                              {tokenAmount != null ? formatCrypto(tokenAmount) : '—'}{' '}
                              {conversionPreview.tokenSymbol}
                            </span>
                            <span className="conversion-rate-info">
                              ${formatUSD(netUsd)} at 1 {conversionPreview.tokenSymbol} = $
                              {formatUSD(rate)}
                            </span>
                            {conversionPreview.isTestnet && (
                              <span className="conversion-testnet-info">
                                Testnet is priced at the live market rate for this asset.
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="info-box">
                  <AlertCircle size={16} />
                  <span>
                    {mode === 'test'
                      ? 'Testnet mode: the amount is converted at the live market rate, the same as live mode.'
                      : 'Live mode: Amount will be converted to selected token at current market rate'}
                  </span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={requestCloseCreateModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-confirm"
                  disabled={createLoading || !balance.hasWalletAddress || parseFloat(createForm.amount) > payoutCeilingUsd || (feePreview && feePreview.blocked)}
                >
                  {createLoading ? 'Creating...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {upsell.isOpen && (
        <PremiumUpsellModal {...upsell.modalProps} onClose={upsell.close} />
      )}

      <style>{`
        .payouts-page {
          padding: 24px;
          /* Panel width lives here so the drawer and its scrim cannot drift
             apart: the scrim stops exactly where the drawer starts. */
          --create-drawer-w: 900px;
        }

        /* ---- Balance + auto-conversion row ---- */
        .ent .po-top {
          display: grid;
          grid-template-columns: minmax(0, 1.55fr) minmax(300px, 1fr);
          gap: 16px;
          margin-bottom: 16px;
          align-items: stretch;
        }

        /* The one saturated surface on the page. Everything else is white on
           grey, so making the balance violet is what makes it the first thing
           the eye lands on without needing to be bigger than it already is. */
        .ent .po-bal {
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border-radius: var(--ent-radius);
          padding: 22px 24px;
          color: #fff;
          /* Darker than the brand violet on purpose. Every figure here is
             white, and white on #6d5df6 is only 4.6:1 — fine alone, but the
             sheen in the radial lifts the surface further and takes the 12px
             labels under AA. These stops hold every element above 4.5:1 at the
             card's lightest point, sheen included: white 5.4, labels 4.8,
             the mint net figure 4.6. */
          background:var(--grad-balance);
          box-shadow: 0 10px 30px -14px rgba(62, 49, 174, 0.55);
        }

        /* ---- artwork ---- */
        .ent .po-bal__art {
          position: absolute;
          inset: 0 0 0 auto;
          width: 46%;
          min-width: 220px;
          pointer-events: none;
        }
        /* Concentric rings the cluster floats over. Drawn with inset rings on a
           single element rather than three stacked divs. */
        .ent .po-bal__rings {
          position: absolute;
          top: 50%; right: 14%;
          width: 270px; height: 270px;
          transform: translateY(-50%);
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.14);
          box-shadow:
            inset 0 0 0 26px rgba(255, 255, 255, 0.045),
            inset 0 0 0 27px rgba(255, 255, 255, 0.11),
            inset 0 0 0 58px rgba(255, 255, 255, 0.035),
            inset 0 0 0 59px rgba(255, 255, 255, 0.08);
        }

        /* Each coin is two layers: the outer element is the disc's edge and its
           coloured glow, the inner face is the lit top surface. Tilting the
           outer and counter-rotating nothing inside is what reads as a struck
           coin seen at an angle rather than a flat circle. */
        .ent .po-bal__coin {
          position: absolute;
          border-radius: 50%;
          background: var(--coin-glow, #fff);
          animation: po-bob 6s ease-in-out infinite;
          filter:
            drop-shadow(0 14px 22px rgba(18, 14, 60, 0.45))
            drop-shadow(0 0 22px color-mix(in srgb, var(--coin-glow, #fff) 55%, transparent));
        }
        .ent .po-bal__coin-face {
          display: grid;
          place-items: center;
          width: 100%; height: 100%;
          border-radius: 50%;
          /* Light from the top-left, and a darker bottom edge for the rim. */
          background:
            radial-gradient(70% 70% at 30% 25%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.78) 55%, rgba(255, 255, 255, 0.6) 100%);
          box-shadow:
            inset 0 2px 3px rgba(255, 255, 255, 0.9),
            inset 0 -4px 8px rgba(30, 22, 90, 0.22);
          transform: translateY(-3px);
        }
        .ent .po-bal__coin img,
        .ent .po-bal__coin-fallback {
          width: 58%; height: 58%; border-radius: 50%; object-fit: contain;
          display: grid; place-items: center;
          color: #fff; font-weight: 700; font-size: 12px;
        }
        /* Three sizes at three depths: the largest reads as nearest. */
        .ent .po-bal__coin-1 { width: 72px; height: 72px; right: 30%; top: 33%; transform: rotate(-10deg); }
        .ent .po-bal__coin-2 { width: 62px; height: 62px; right: 13%; top: 13%; transform: rotate(12deg); animation-delay: -2s; }
        .ent .po-bal__coin-3 { width: 84px; height: 84px; right: 16%; top: 52%; transform: rotate(-6deg); animation-delay: -4s; }
        /* The bob has to carry the tilt with it, or the animation resets each
           coin to 0deg on its first frame. */
        @keyframes po-bob {
          0%, 100% { translate: 0 0; }
          50%      { translate: 0 -9px; }
        }
        /* color-mix is recent; without it the second drop-shadow is dropped and
           the coins simply lose their coloured halo. */
        @supports not (color: color-mix(in srgb, red 50%, transparent)) {
          .ent .po-bal__coin { filter: drop-shadow(0 14px 22px rgba(18, 14, 60, 0.45)); }
        }

        /* ---- balance ---- */
        .ent .po-bal__head {
          position: relative; z-index: 1;
          display: flex; align-items: center; gap: 8px;
        }
        .ent .po-bal__label {
          font-size: 13.5px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.92);
        }
        .ent .po-bal__eye {
          display: inline-grid; place-items: center;
          width: 26px; height: 26px;
          border: none; border-radius: 8px;
          background: rgba(255, 255, 255, 0.14);
          color: rgba(255, 255, 255, 0.9);
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .ent .po-bal__eye:hover { background: rgba(255, 255, 255, 0.24); }

        .ent .po-bal__figure { position: relative; z-index: 1; margin: 8px 0 22px; }
        .ent .po-bal__amt {
          font-size: 42px;
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: #fff;
          font-variant-numeric: tabular-nums;
        }
        .ent .po-bal__amt--hidden { letter-spacing: 0.08em; }

        /* ---- plan / fee / net ---- */
        .ent .po-bal__stats {
          position: relative; z-index: 1;
          margin-top: auto;
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }
        .ent .po-bal__cell { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .ent .po-bal__ico {
          display: inline-grid; place-items: center;
          width: 32px; height: 32px; flex: none;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.15);
          color: rgba(255, 255, 255, 0.95);
        }
        .ent .po-bal__cellbody { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .ent .po-bal__k { font-size: 12px; color: rgba(255, 255, 255, 0.88); }
        .ent .po-bal__v {
          font-size: 14.5px; font-weight: 700; color: #fff;
          font-variant-numeric: tabular-nums;
        }
        .ent .po-bal__fee { font-weight: 500; color: rgba(255, 255, 255, 0.88); }
        /* Light mint, not the page's --ok: the darker green reads almost black
           on violet. #a8ffd9 clears 5.2:1 on the lightest point of the card —
           #7df5c3 only just made 4.5 and lost it under the sheen. */
        .ent .po-bal__v--net { color: #a8ffd9; }
        .ent .po-bal__div { width: 1px; height: 30px; background: rgba(255, 255, 255, 0.2); }

        /* ---- auto-conversion ---- */
        .ent .po-autoconv {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          background: var(--ent-card, #fff);
          border: 1px solid var(--ent-border, #e6e8ee);
          border-radius: var(--ent-radius);
          box-shadow: var(--ent-shadow);
          padding: 20px;
          margin: 0;
        }
        .ent .po-autoconv__icon {
          display: inline-grid; place-items: center;
          width: 44px; height: 44px; flex: none;
          border-radius: 50%;
          background: var(--brand-100, #ece9ff);
          color: var(--brand-600, #6d5df6);
        }
        .ent .po-autoconv__body { flex: 1; min-width: 0; }
        .ent .po-autoconv__title {
          display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
          font-size: 15px; font-weight: 700;
          color: var(--ent-text, #14161c);
        }
        .ent .po-autoconv__title .priority-chip { margin-left: 0; }
        /* State as a chip rather than the first word of the sentence: it is what
           the merchant scans for and should not need reading to find. */
        .ent .po-autoconv__state {
          font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
          padding: 3px 8px; border-radius: 999px;
          background: var(--ent-neutral-bg, #eef0f4);
          color: var(--ent-muted, #5b6170);
        }
        .ent .po-autoconv__state.is-on { background: var(--ok-soft, #e6f8f1); color: var(--ok-ink, #0a8f64); }
        .ent .po-autoconv__desc {
          display: block;
          font-size: 13px; line-height: 1.55;
          color: var(--ent-muted, #5b6170);
          margin-top: 6px;
        }
        .ent .po-autoconv__cta {
          display: inline-flex; align-items: center; gap: 5px;
          flex: none; align-self: center;
          height: 38px; padding: 0 16px;
          border: 1px solid var(--brand-400, #a99cff);
          border-radius: 999px;
          background: var(--ent-card, #fff);
          color: var(--brand-600, #6d5df6);
          font: inherit; font-size: 13.5px; font-weight: 600;
          white-space: nowrap; cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .ent .po-autoconv__cta:hover { background: var(--brand-100, #ece9ff); border-color: var(--brand-600, #6d5df6); }

        .ent .po-nudge {
          display: inline-flex; align-items: center; gap: 5px;
          margin-top: 10px;
          padding: 5px 11px;
          border-radius: var(--ent-radius-pill);
          background: var(--ent-amber-bg);
          color: var(--ent-amber);
          font-size: 12px; font-weight: 600;
        }
        .ent .po-nudge svg { width: 14px; height: 14px; }

        @media (prefers-reduced-motion: reduce) {
          .ent .po-bal__coin { animation: none; }
        }

        @media (max-width: 1180px) {
          .ent .po-top { grid-template-columns: minmax(0, 1fr); }
          /* The artwork needs a wide card to sit clear of the figures; once the
             card narrows it would crowd them instead. */
          .ent .po-bal__art { display: none; }
        }
        @media (max-width: 640px) {
          .ent .po-bal { padding: 18px; }
          .ent .po-bal__amt { font-size: 34px; }
          .ent .po-bal__stats { gap: 12px; }
          .ent .po-bal__div { display: none; }
          .ent .po-bal__cell { flex: 1 1 45%; }
          .ent .po-autoconv { flex-wrap: wrap; padding: 16px; }
          .ent .po-autoconv__cta { width: 100%; justify-content: center; min-height: 44px; }
        }

        /* keep the mt-4 helper for the empty-state button */
        .ent .mt-4 { margin-top: 1rem; }

        /* testnet chip beside payout id */
        .ent .pro-table .testnet-badge {
          display: inline-block;
          padding: 0.125rem 0.375rem;
          background: var(--ent-amber-bg);
          color: var(--ent-amber);
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        /* P3: priority chip beside payout id */
        .ent .pro-table .priority-badge {
          display: inline-block;
          padding: 0.125rem 0.375rem;
          background: var(--brand-100, rgba(124, 58, 237, 0.12));
          color: var(--brand-700, #6d28d9);
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        /* spacing between kit blocks */
        .ent .kpi-strip { margin: 16px 0; }
        .ent .filter-tabs { margin-bottom: 14px; }
        .ent .pro-table-wrap { margin-bottom: 16px; }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }

        .page-header h1 {
          margin: 0 0 0.25rem 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        /* Balance Card */
        .balance-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          background: var(--grad-primary);
          border-radius: var(--r-lg);
          margin-bottom: 1.5rem;
          color: white;
        }

        .balance-card-content {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .balance-main {
          display: flex;
          flex-direction: column;
        }

        .balance-label {
          font-size: 0.85rem;
          opacity: 0.9;
        }

        .balance-amount {
          font-size: 2rem;
          font-weight: 700;
        }

        .balance-pending {
          font-size: 0.8rem;
          opacity: 0.8;
          margin-top: 0.25rem;
        }

        .balance-wallet {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          opacity: 0.9;
          background: rgba(255, 255, 255, 0.15);
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          margin-top: 0.5rem;
        }

        .balance-stats {
          display: flex;
          gap: 2rem;
        }

        .balance-stat {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .balance-stat-label {
          font-size: 0.75rem;
          opacity: 0.8;
        }

        .balance-stat-value {
          font-size: 1.1rem;
          font-weight: 600;
        }

        /* Modal Balance Card */
        .modal-balance-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .modal-balance-info {
          display: flex;
          flex-direction: column;
        }

        .modal-balance-label {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .modal-balance-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .modal-balance-sub {
          font-size: 0.75rem;
          color: var(--text-secondary);
          font-variant-numeric: tabular-nums;
        }

        .modal-wallet-info {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        /* Warning Box */
        .warning-box {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 0.875rem;
          background: var(--warn-soft);
          border: 1px solid var(--warn);
          border-radius: var(--r-sm);
          margin-bottom: 1rem;
        }

        .warning-box svg {
          color: #ffffff;
          flex-shrink: 0;
        }

        .warning-box span {
          font-size: 0.8rem;
          color: var(--warn-ink);
        }

        /* BUG-10: auto-conversion lock note in the create-payout form */
        .autoconv-lock-note {
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.75rem 0.875rem;
          background: var(--ent-accent-soft, #eef0ff);
          border: 1px solid var(--ent-border, #e6e8ee);
          border-radius: 8px;
          margin-bottom: 1rem;
          font-size: 0.8rem;
          color: var(--text-primary);
        }
        .autoconv-lock-note svg {
          color: var(--ent-accent, #6d5dfc);
          flex-shrink: 0;
        }

        /* Form Error */
        .form-error {
          display: block;
          font-size: 0.75rem;
          color: var(--err-ink);
          margin-top: 0.375rem;
        }

        /* Form Row */
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        /* Fee Calculation Box */
        .fee-calculation-box {
          padding: 1rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .fee-calculation-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid var(--border-color);
        }

        .fee-calculation-header span:first-child {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .fee-percentage-badge {
          padding: 0.25rem 0.5rem;
          background: var(--warn-soft);
          color: var(--warn-ink);
          border-radius: var(--r-sm);
          font-size: 0.7rem;
          font-weight: 600;
        }

        .premium-pricing-badge {
          padding: 0.25rem 0.5rem;
          background: var(--brand-100);
          color: var(--brand-600);
          border-radius: var(--r-sm);
          font-size: 0.7rem;
          font-weight: 600;
        }

        .fee-blocked-reason {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid var(--border-color);
        }

        .fee-blocked-reason svg {
          color: var(--err-ink);
          flex-shrink: 0;
          margin-top: 0.1rem;
        }

        .fee-blocked-reason span {
          font-size: 0.75rem;
          color: var(--err-ink);
          line-height: 1.4;
        }

        .fee-calculation-rows {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .fee-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .fee-row.fee-deduction {
          color: var(--warn-ink);
        }

        .fee-row__meta {
          color: var(--text-muted);
          font-weight: 400;
        }

        /* Second line under "Processing Fee", explaining a minimum-driven fee. */
        .fee-row__note {
          display: block;
          color: var(--text-muted);
          font-weight: 400;
          font-size: 0.72rem;
          margin-top: 0.1rem;
        }

        .fee-row.net-amount {
          padding-top: 0.5rem;
          border-top: 1px dashed var(--border-color);
          font-weight: 600;
          color: var(--ok-ink);
        }

        .fee-row.net-amount span:last-child {
          font-size: 1rem;
        }

        /* Conversion Preview */
        .conversion-preview {
          padding: 1rem;
          background: var(--ok-soft);
          border: 1px solid var(--ok);
          border-radius: var(--r-sm);
          margin-bottom: 1rem;
        }

        .conversion-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .conversion-header span:first-child {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .conversion-loading {
          font-size: 0.7rem;
          color: var(--text-muted);
        }

        .conversion-amount {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .conversion-token-amount {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--ok-ink);
        }

        .conversion-rate-info,
        .conversion-testnet-info {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .page-header p {
          margin: 0;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .btn-primary {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: var(--accent-primary);
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          color: white;
          cursor: pointer;
        }

        .btn-primary:hover {
          opacity: 0.9;
        }

        .mt-4 {
          margin-top: 1rem;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .stat-card {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1.25rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
        }

        .stat-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 12px;
          color: white;
          flex-shrink: 0;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .stat-title {
          font-size: 0.8rem;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        /* Status Tabs */
        .status-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 0;
        }

        .status-tab {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-secondary);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          margin-bottom: -1px;
        }

        .status-tab:hover {
          color: var(--text-primary);
        }

        .status-tab.active {
          color: var(--accent-primary);
          border-bottom-color: var(--accent-primary);
        }

        .tab-count {
          padding: 0.125rem 0.5rem;
          background: var(--bg-secondary);
          border-radius: 10px;
          font-size: 0.75rem;
        }

        .status-tab.active .tab-count {
          background: var(--brand-100);
          color: var(--brand-600);
        }

        /* Table Toolbar */
        .table-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .toolbar-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-toolbar {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 0.875rem;
          color: var(--text-primary);
          cursor: pointer;
        }

        .btn-toolbar:hover {
          background: var(--bg-secondary);
        }

        .btn-clear-filters {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem 0.75rem;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          font-size: 0.8rem;
          cursor: pointer;
        }

        .btn-clear-filters:hover {
          color: var(--accent-primary);
        }

        /* Dropdown */
        .dropdown-container {
          position: relative;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 0.5rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          z-index: 100;
          min-width: 180px;
        }

        .dropdown-section {
          padding: 0.5rem;
        }

        .dropdown-header {
          padding: 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.5rem;
          background: transparent;
          border: none;
          border-radius: 4px;
          font-size: 0.875rem;
          color: var(--text-primary);
          cursor: pointer;
          text-align: left;
        }

        .dropdown-item:hover {
          background: var(--bg-secondary);
        }

        .dropdown-item.active {
          color: var(--accent-primary);
        }

        /* Table */
        .payout-table-container {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          overflow-x: scroll;
        }

        .payout-table {
          width: 100%;
          border-collapse: collapse;
        }

        .payout-table th {
          padding: 0.875rem 1rem;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--border-color);
          background: var(--bg-secondary);
        }

        .payout-table th.sortable {
          cursor: pointer;
        }

        .payout-table th.sortable:hover {
          color: var(--text-primary);
        }

        .payout-table th svg {
          margin-left: 0.25rem;
          vertical-align: middle;
        }

        .payout-table th svg.rotate-180 {
          transform: rotate(180deg);
        }
 .payout-table tr {
      border-bottom: 1px solid var(--border-color);
     }
        .payout-table td {
          padding: 1rem;
          // border-bottom: 1px solid var(--border-color);
          font-size: 0.875rem;
          color: var(--text-primary);
              white-space: normal;
        }

        .payout-table tbody tr:last-child td {
          border-bottom: none;
        }

        .request-id-cell .request-id-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .request-id {
          font-weight: 500;
          font-family: monospace;
        }

        .testnet-badge {
          display: inline-block;
          padding: 0.125rem 0.375rem;
          background: var(--warn-soft);
          color: var(--warn-ink);
          border-radius: var(--r-sm);
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        .priority-badge {
          display: inline-block;
          padding: 0.125rem 0.375rem;
          background: var(--brand-100, rgba(124, 58, 237, 0.12));
          color: var(--brand-700, #6d28d9);
          border-radius: var(--r-sm);
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        .priority-chip {
          display: inline-block;
          margin-left: 0.5rem;
          padding: 0.1rem 0.5rem;
          background: var(--brand-100, rgba(124, 58, 237, 0.12));
          color: var(--brand-700, #6d28d9);
          border-radius: var(--r-pill, 999px);
          font-size: 0.65rem;
          font-weight: 700;
          vertical-align: middle;
        }

        .amount-wrapper {
          display: flex;
          flex-direction: column;
        }

        .amount-usd {
          font-weight: 600;
          color: var(--text-primary);
        }

        .amount-token {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .token-cell {
          vertical-align: middle;
        }

        .token-badge {
          display: inline-block;
          padding: 0.125rem 0.5rem;
          background: var(--brand-100);
          color: var(--brand-600);
          border-radius: var(--r-sm);
          font-size: 0.75rem;
          font-weight: 500;
        }

        .network-name {
          display: block;
          font-size: 0.7rem;
          color: var(--text-muted);
          margin-top: 0.25rem;
        }

        .address-cell {
          font-family: monospace;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .date-cell {
          color: var(--text-secondary);
          white-space: nowrap;
        }

        // .actions-cell {
        //   text-align: right;
        // }

        .btn-view {
          padding: 0.375rem 0.75rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 0.8rem;
          color: var(--text-primary);
          cursor: pointer;
        }

        .btn-view:hover {
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: white;
        }

        /* Status Badge */
        .status-badge {
          display: inline-flex;
          padding: 0.25rem 0.625rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-pending {
          background: var(--warn-soft);
          color: var(--warn-ink);
        }

        .status-approved {
          background: var(--info-soft);
          color: var(--info-ink);
        }

        .status-processing {
          background: var(--brand-100);
          color: var(--brand-600);
        }

        .status-completed {
          background: var(--ok-soft);
          color: var(--ok-ink);
        }

        .status-rejected {
          background: var(--err-soft);
          color: var(--err-ink);
        }

        .status-cancelled {
          background: var(--bg-inset);
          color: var(--t-500);
        }

        /* Loading & Empty States */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          gap: 1rem;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--border-color);
          border-top-color: var(--accent-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: var(--text-secondary);
        }

        .empty-state svg {
         color: white;
          // margin-bottom: 1rem;
        }

        .empty-state p {
          margin: 0 0 0.25rem 0;
          font-weight: 500;
          color: var(--text-primary);
        }

        .empty-state span {
          font-size: 0.875rem;
        }

        /* Pagination */
        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          margin-top: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .pagination-info {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .page-size-select {
          padding: 0.375rem 0.5rem;
          border: 1px solid var(--border-color);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.8rem;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pagination-btn {
          width: 36px;
          height: 36px;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 14px;
          color: var(--text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .page-numbers {
          display: flex;
          align-items: center;
          gap: 4px;
          margin: 0 4px;
        }

        .page-number {
          min-width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .page-number:hover:not(.active) {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .page-number.active {
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: white;
        }

        /* Drawer */
        .drawer-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
        }

        .request-detail-drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: 450px;
          max-width: 100%;
          height: 100vh;
          background: var(--bg-secondary);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        .drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          /* 0.75rem 1rem to match the Create Invoice drawer exactly. */
          padding: 0.75rem 1rem;
          background: var(--bg-primary);
          border-bottom: 1px solid var(--border-color);
          /* Carried over from .modal-header, which this replaces: the panel is
             a flex column, so without this the header would be absorbed into
             the scroll area and the title could scroll out of view. */
          flex-shrink: 0;
        }

        .drawer-header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .close-btn {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          /* Centres the glyph in its hit area; the invoice drawer's button does
             the same, and without it the X sits off-centre. */
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .drawer-title-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        /* Tinted square behind the glyph, same as the invoice drawer. */
        .drawer-icon-wrapper {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          background: rgba(124, 58, 237, 0.1);
          border-radius: var(--r-sm, 8px);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent-primary, #7c3aed);
        }

        .drawer-title-section h2 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          /* The monospace here was for the payout ID the removed detail drawer
             used to show. On a title it just renders "Request Payout" in the
             wrong face, so it is dropped -- nothing else uses this selector. */
        }

        .drawer-content {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
        }

        .detail-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .detail-card h3 {
          margin: 0 0 1rem 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .detail-card p {
          margin: 0;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .rejection-card {
          border-color: var(--err);
          background: var(--err-soft);
        }

        .rejection-card h3 {
          color: var(--err-ink);
        }

        .amount-display {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .amount-large {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .token-amount {
          font-size: 1rem;
          color: var(--text-secondary);
        }

        .conversion-rate {
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .detail-info-grid {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .detail-info-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .detail-info-item svg {
          color: var(--text-muted);
          margin-top: 0.125rem;
          flex-shrink: 0;
        }

        .detail-info-item label {
          display: block;
          font-size: 0.7rem;
          color: var(--text-secondary);
          margin-bottom: 0.125rem;
        }

        .detail-info-item span {
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        .address-full {
          word-break: break-all;
          font-family: monospace;
          font-size: 0.8rem !important;
        }

        .tx-hash-link {
          color: var(--accent-primary);
          text-decoration: none;
          word-break: break-all;
          font-family: monospace;
          font-size: 0.8rem;
        }

        .tx-hash-link:hover {
          text-decoration: underline;
        }

        /* Timeline */
        .timeline {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .timeline-item {
          display: flex;
          gap: 1rem;
          padding: 0.75rem 0;
          position: relative;
        }

        .timeline-item:not(:last-child)::before {
          content: '';
          position: absolute;
          left: 5px;
          top: 2rem;
          bottom: -0.5rem;
          width: 2px;
          background: var(--border-color);
        }

        .timeline-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          flex-shrink: 0;
          margin-top: 0.25rem;
        }

        .timeline-dot.completed {
          background: var(--ok);
          border-color: var(--ok);
        }

        .timeline-content {
          display: flex;
          flex-direction: column;
        }

        .timeline-title {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .timeline-date {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        /* Action Buttons */
        .action-buttons {
          margin-top: 1rem;
        }

        .btn-action {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-cancel-request {
          background: var(--err-soft);
          color: var(--err-ink);
          border: 1px solid var(--err);
        }

        .btn-cancel-request:hover {
          background: var(--err);
          color: white;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1001;
        }

        /* Scrim for the payout drawer. Refines .drawer-overlay above, and relies
           on document order to do it: both are single-class selectors, so they
           tie on specificity and only position in the sheet decides.

           right: var(--create-drawer-w) is the point of this rule. The scrim
           ends at the drawer's leading edge instead of running underneath it,
           so the panel can never be dimmed by it, whatever happens to z-index
           ordering later. The dim then reads as exactly what it means: the page
           behind the panel is inert. */
        .create-drawer-overlay {
          right: var(--create-drawer-w);
          /* Above the page, below the panel (.create-drawer is 1002). */
          z-index: 1001;
          /* Shorter than the panel: the dim should be established by the time
             the panel finishes arriving, not still be deepening after it. */
          animation: drawer-scrim-in 260ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;

          /* Blur what is behind the scrim so the panel reads as the focused
             layer rather than just the brightest one. -webkit- prefix is still
             required for Safari.

             The blur only ever covers the page: this scrim stops at the panel's
             leading edge (right: var(--create-drawer-w) above), and the panel
             sits above it, so the form itself is never softened.

             No keyframe change is needed for the fade -- the element's own
             opacity ramp composites its blurred backdrop along with it, so the
             blur eases in with the dim. */
          -webkit-backdrop-filter: blur(6px);
          backdrop-filter: blur(6px);
        }

        /* Progressive enhancement, not a swap: the 0.5 dim inherited from
           .drawer-overlay stays as the fallback for browsers with no
           backdrop-filter, and is lightened only where the blur actually
           renders -- with both at full strength the page behind goes muddy and
           the blur stops reading as depth. */
        @supports (backdrop-filter: blur(6px)) or
          (-webkit-backdrop-filter: blur(6px)) {
          .create-drawer-overlay {
            background: rgba(15, 23, 42, 0.42);
          }
        }

        .create-drawer-overlay.is-closing {
          animation: drawer-scrim-out 220ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
          pointer-events: none;
        }

        /* Right-side drawer, replacing the centred dialog this used to be.
           Geometry is taken from .request-detail-drawer above so both panels on
           this page slide in identically; it reuses that same slideIn keyframe. */
        .create-drawer {
          position: fixed;
          top: 0;
          right: 0;
          height: 100vh;
          /* Reads the variable so the panel and the scrim inset below cannot
             drift apart. Same 900px as the invoice drawer. */
          width: var(--create-drawer-w);
          max-width: 100%;
          background: var(--bg-primary);
          z-index: 1002;
          box-shadow: -4px 0 24px rgba(0, 0, 0, 0.18);

          /* Header and footer are pinned; only .modal-content scrolls, so the
             title, the close button and the Cancel / Submit Request pair stay
             on screen at any window height. */
          display: flex;
          flex-direction: column;
          overflow: hidden;

          animation: drawer-in 320ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
        }

        /* Leaving. pointer-events is dropped so a fast second click cannot land
           on a panel that is already on its way out. */
        .create-drawer.is-closing {
          animation: drawer-out 220ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
          pointer-events: none;
        }

        /* Shared drawer motion. Same --mx-ease-glide curve as the page-section
           transitions (defined on :root in PageTransition.css, which
           DashboardLayout imports, so it is always in scope here).

           This is a cross-fade, not a slide. The travel is 28px, not the full
           100% the panel used to cover: over 380ms that long a journey is what
           read as sluggish rather than smooth, and it left the panel visibly
           moving after the eye had already found it. Opacity now does the work
           and the small offset only hints at direction.

           Out is faster than in (220ms vs 320ms) for the same reason it is in
           PageTransition.css: the exit is dead time, so it should be brisk,
           while the entrance is where content actually arrives.

           Only opacity and transform are animated, so nothing re-lays-out
           mid-fade, and both end on 'transform: none' rather than
           translate3d(0,0,0) so the compositing layer is released once the
           panel settles. */
        @keyframes drawer-in {
          from {
            opacity: 0;
            transform: translate3d(28px, 0, 0);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }

        @keyframes drawer-out {
          from {
            opacity: 1;
            transform: none;
          }
          to {
            opacity: 0;
            transform: translate3d(28px, 0, 0);
          }
        }

        /* The scrim used to appear at full strength in a single frame, which is
           what made the opening read as abrupt however smooth the panel was. */
        @keyframes drawer-scrim-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes drawer-scrim-out {
          from { opacity: 1; }
          to { opacity: 0; }
        }


        /* The form, not .modal-content, is the flex child of the panel, so it
           has to pass the height constraint through to the scroll region.
           Without flex: 1 + min-height: 0 here, .modal-content never gets a
           bounded box and the footer is pushed off the bottom of the drawer. */
        .create-drawer form {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }

        /* Must come after the .create-drawer rule above: a media query adds no
           specificity, so this only wins by document order. */
        @media (prefers-reduced-motion: reduce) {
          .create-drawer,
          .create-drawer.is-closing,
          .create-drawer-overlay,
          .create-drawer-overlay.is-closing {
            animation: none;
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid var(--border-color);
          /* Never absorbed into the scroll area. */
          flex-shrink: 0;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary);
        }
        // .create-drawer .modal-content{overflow-y:unset;}
        .modal-content {
          padding: 1.25rem;
          /* Claims the leftover height so the footer sits on the bottom edge
             of a full-height drawer instead of floating directly under the
             form with blank panel beneath it. */
          flex: 1;
          /* The one scrollable region in the drawer. min-height: 0 is
             load-bearing, not defensive: a flex item defaults to
             min-height: auto and so refuses to shrink below its content, which
             would make this box grow and push the footer out instead of
             scrolling. Same trap that broke .page-content, see
             PageTransition.css. */
          overflow-y: auto;
          min-height: 0;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group:last-child {
          margin-bottom: 0;
        }

        .form-group label {
          display: block;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 0.875rem;
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .form-group select {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .form-group select option {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .form-hint {
          display: block;
          font-size: 0.75rem;
          color: var(--text-muted);
          margin-top: 0.375rem;
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        .checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .info-box {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 0.875rem;
          background: var(--info-soft);
          border-radius: var(--r-sm);
          margin-top: 1rem;
        }

        .info-box svg {
          color: var(--info-ink);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .info-box span {
          font-size: 0.8rem;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem;
          border-top: 1px solid var(--border-color);
          /* Submit / Cancel stay visible however long the form gets. */
          flex-shrink: 0;
        }

        .btn-cancel {
          padding: 0.75rem 1.25rem;
          background: transparent;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 0.875rem;
          color: var(--text-primary);
          cursor: pointer;
          font-family: var(--iv-sans);
        }

        .btn-cancel:hover {
          background: var(--bg-secondary);
        }

        .btn-confirm {
          padding: 0.75rem 1.25rem;
          border: none;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          background: var(--grad-primary);
          color: #ffffff;
          border-color: transparent;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 20px rgba(109, 93, 246, 0.35);
          border-radius: var(--r-md);
          font-family: var(--iv-sans);
        }
    
        .btn-confirm:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 10px 24px rgba(109, 93, 246, 0.42);
        }

        .btn-confirm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Responsive */
        @media(max-width: 1200px){
            .create-drawer .modal-content{overflow-y:scroll;}
        }
        @media (max-width: 768px) {
          .create-drawer{
            width:100%;
          }

          /* Full-width panel, so there is no uncovered strip to dim; without
             this the scrim would collapse to zero width and the backdrop would
             vanish during the slide-in. */
          .create-drawer-overlay {
            right: 0;
          }

          /* responsive/components.css granted these to .create-modal at <=576px.
             Renaming the class drops out of those selectors, so the mobile touch
             targets are restated here rather than silently lost. */
          .create-drawer input,
          .create-drawer select,
          .create-drawer button {
            min-height: 44px;
            font-size: 16px;
          }
          .page-header {
            flex-direction: column;
            gap: 1rem;
          }

          .btn-primary {
            width: 100%;
            justify-content: center;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .request-detail-drawer {
            width: 100%;
          }
          .pagination-container {
            flex-direction: column;
            align-items: center;
          }
          .modal-content {
            max-height: 70vh;
            overflow-y: auto;
          }
          .modal-balance-card{
            display: inline-grid;
            width:100%;
            gap:10px;
          }
        }
      `}</style>
    </div>
  );
};

export default Payouts;
